import React, { useContext, useEffect, useState } from "react";
import SiteHistoryCard from "../../components/SiteHistoryCard";
import SiteTable from "../../components/SiteTable";
import { Link } from "react-router-dom";
import Preloader from "../../components/preloader";

// react-bootstrap components
import {
  Card,
  Container,
  Row,
  Col,
} from "react-bootstrap";
import axios from "../../utils/axiosConfig";
import { useAuth0 } from "@auth0/auth0-react";
import SitewideDataContext from "../../utils/sitewideContext";
import * as Sentry from "@sentry/react";

import "../../constants/service_constants";

const MIN_LOADER_MS = 1000;

function Site() {
  const [data, setData] = useState([]);
  const [title, setTitle] = useState("");
  const [siteTime, setSiteTime] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const { getAccessTokenSilently } = useAuth0();
  const queryParameters = new URLSearchParams(window.location.search);
  const site_id = queryParameters.get("site_id");
  const { sitewideData, setSitewideData } = useContext(SitewideDataContext);

  // Breadcrumb: show site name in nav bar
  useEffect(() => {
    if (title) {
      setSitewideData((prev) => ({ ...prev, breadcrumbExtra: { label: title } }));
    }
    return () => {
      setSitewideData((prev) => ({ ...prev, breadcrumbExtra: null }));
    };
  }, [title, setSitewideData]);

  const setNewSiteTime = (newSiteTime) => {
    setSiteTime(newSiteTime);
  };

  useEffect(() => {
    if (!site_id) {
      setData([]);
      setTitle("");
      return;
    }

    const loadStart = Date.now();
    setHistoryLoading(true);

    async function getData() {
      let pages = [];
      try {
        const accessToken = await getAccessTokenSilently();
        let response = null;

        if (siteTime) {
          response = await axios.get(API_ADDRESS + SITE_RECENT_ENDPOINT + site_id + "/" + siteTime, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
        } else {
          response = await axios.get(API_ADDRESS + SITE_RECENT_ENDPOINT + site_id, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
        }

        if (response?.data?.site_crawl?.pages && "" in response.data.site_crawl.pages) {
          delete response.data.site_crawl.pages[""];
        }

        pages = Object.keys(response.data.site_crawl.pages || {}).map((page_name) => {
          return {
            page_name,
            pdf: response.data.site_crawl.pages[page_name].pdf,
            pdf_location: response.data.site_crawl.pages[page_name].pdf_location,
          };
        });

        setTitle(response.data.site_crawl.address);
        setData(
          pages.map((prop, key) => {
            if (prop.pdf === true) {
              return {
                id: key,
                page: (
                  <a
                    href={CDN3_ADDRESS + prop.pdf_location}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-body"
                    title={prop.page_name}
                  >
                    <i className="fal fa-external-link" aria-hidden="true" />
                    &nbsp;&nbsp;{prop.page_name}
                  </a>
                ),
              };
            }
            return {
              id: key,
              page: (
                <Link
                  to={
                    "/pagehistory?site_id=" +
                    response.data.site.site_id +
                    "&page_id=" +
                    encodeURIComponent(prop.page_name) +
                    "&endtime=" +
                    siteTime +
                    "&starttime=null&site_domain=" +
                    response.data.site_crawl.address
                  }
                  className="text-body"
                  title={prop.page_name}
                >
                  {prop.page_name}
                </Link>
              ),
            };
          })
        );
      } catch (e) {
        Sentry.captureException(e);
        setData([]);
      } finally {
        setSitewideData((prev) => ({ ...prev, overloading: false }));
        const elapsed = Date.now() - loadStart;
        const remaining = Math.max(0, MIN_LOADER_MS - elapsed);
        setTimeout(() => setHistoryLoading(false), remaining);
      }
    }

    getData();
  }, [site_id, siteTime]);

  return (
    <>
      <Container fluid className="wmp-fade-in">
        <div className="page-back-row">
          <Link to="/dashboard" className="page-back-link">
            <i className="fa-solid fa-chevron-left" aria-hidden="true" />
            <span>Back to dashboard</span>
          </Link>
        </div>
        <Row>
          <Col lg="9">
            <Card className="site-pages-card">
              <Card.Header className="site-pages-card__header">
                <Card.Title as="h4" className="site-pages-card__title">Pages</Card.Title>
                {site_id && (
                  <Link to={"/sitedetails?site_id=" + site_id} className="btn-manage">
                    <i className="fa-solid fa-gear" aria-hidden="true" />
                    <span>Manage site</span>
                  </Link>
                )}
              </Card.Header>
              <Card.Body className="position-relative">
                {historyLoading ? (
                  <div className="site-pages-loader">
                    <Preloader inline overlay={false} text textOverride="Loading crawl…" />
                  </div>
                ) : (
                  <SiteTable
                    data={data}
                    columns={[
                      {
                        Header: "Page",
                        accessor: "page",
                        style: { width: "80%" },
                        id: "0",
                        filter: "text",
                      },
                    ]}
                    title={title}
                    className="-striped -highlight none"
                    siteId={site_id}
                  />
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col lg="3">
            <SiteHistoryCard
              setNewSiteTime={setNewSiteTime}
            />
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default Site;
